import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { type EventDetail, type ProgramItem, useEvent, useEventTypes } from '../api/events';
import { useMembership } from '../auth/session';
import { ErrorView, Loading } from '../components/Guards';
import { dateLocale } from '../i18n';
import { daysUntil, formatDayMonth, formatEventDates, formatLength } from '../lib/eventDates';
import ui from '../styles/ui.module.css';
import styles from './EventPage.module.css';
import { EventRoster } from './EventRoster';

/**
 * Director: one event (a simplified wireframe 04): its details and programme, and every singer
 * in the line-up with their answer, note and readiness, all read-only. The programme is edited on
 * its own page ("Edit repertoire"), and so is the line-up ("Edit line-up": leave a member out with
 * a reason only they see, or take them back).
 */
export function EventPage() {
  const { eventId } = useParams();
  const membership = useMembership();
  const event = useEvent(membership?.choirId, eventId);
  if (!membership) return null;
  if (event.isPending) return <Loading />;
  if (event.isError || !event.data) return <ErrorView onRetry={() => void event.refetch()} />;
  return <EventView event={event.data} />;
}

function EventView({ event }: { event: EventDetail }) {
  const { t } = useTranslation();
  const locale = dateLocale();
  const types = useEventTypes();
  const s = event.summary;
  const days = daysUntil(event.startsOn);
  const fields = types.data?.find((x) => x.type === event.type)?.fields ?? [];
  const facts = [
    event.callAndDress,
    ...fields
      .filter((f) => event.details[f.key] !== undefined)
      .map((f) => {
        const value = event.details[f.key];
        const shown = f.kind === 'DATE' ? formatDayMonth(String(value), locale) : String(value);
        return `${t(`eventForm.fields.${f.key}`)}: ${shown}`;
      }),
  ].filter(Boolean);

  return (
    <div className={ui.page}>
      <Link to="/events" className={styles.back}>
        {t('eventPage.back')}
      </Link>
      <div className={ui.pageHeader}>
        <div>
          <div className={styles.titleRow}>
            <h1 data-testid="event-title">{event.name}</h1>
            <span className={`${ui.chip} ${ui.chipNeutral}`}>{t(`eventTypes.${event.type}`)}</span>
            <span className={`${ui.chip} ${ui.chipSoft}`}>{event.limitedLineup ? t('eventPage.lineupLimited') : t('eventPage.lineupWhole')}</span>
          </div>
          <div className={ui.subtitle}>
            {[
              formatEventDates(event.startsOn, event.endsOn, locale),
              event.venue,
              days === 0 ? t('eventPage.today') : days > 0 ? t('eventPage.daysLeft', { count: days }) : null,
              event.replyDeadline ? t('eventPage.replyBy', { date: formatDayMonth(event.replyDeadline, locale) }) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
          {facts.length > 0 && <div className={ui.subtitle}>{facts.join(' · ')}</div>}
        </div>
        <div className={styles.actions}>
          <Link to={`/events/${event.id}/edit`} className={ui.button}>
            {t('eventPage.edit')}
          </Link>
          {event.program.some((i) => i.kind === 'SONG') && (
            <Link to={`/events/${event.id}/rehearsal`} className={ui.button}>
              {t('rehearsal.open')}
            </Link>
          )}
          <Link to={`/events/${event.id}/lineup`} className={ui.button}>
            {t('eventPage.editLineup')}
          </Link>
          {event.readinessTracking && (
            <Link to={`/events/${event.id}/repertoire`} className={`${ui.button} ${ui.primary}`}>
              {t('events.editRepertoire')}
            </Link>
          )}
        </div>
      </div>

      <div className={styles.stats}>
        <section className={ui.card} data-testid="event-attending">
          <span className={ui.muted}>{t('eventPage.attending')}</span>
          <span className={styles.big}>
            {s.attending} / {s.lineupCount}
          </span>
          <span className={ui.muted}>
            {[
              s.noReply > 0 ? t('events.noReplyCount', { count: s.noReply }) : null,
              s.notAttending > 0 ? t('eventPage.notAttendingCount', { count: s.notAttending }) : null,
            ]
              .filter(Boolean)
              .join(' · ') || '\u00a0'}
          </span>
        </section>
        {event.readinessTracking && (
          <section className={ui.card} data-testid="event-ready">
            <span className={ui.muted}>{t('eventPage.readyAll')}</span>
            <span className={styles.big}>
              {s.readyAll} / {s.attending}
            </span>
            <span className={ui.muted}>{t('eventPage.readyAllDetail')}</span>
          </section>
        )}
      </div>

      {event.readinessTracking && (
        <section className={ui.card}>
          <div className={styles.cardHeader}>
            <h2 className={ui.cardTitle}>{t('eventPage.program')}</h2>
            {event.program.length > 0 && (
              <span>
                {t('eventPage.total')} <strong>{formatLength(event.program.reduce((sum, i) => sum + (i.durationSec ?? 0), 0))}</strong>
              </span>
            )}
          </div>
          {event.program.length === 0 ? (
            <p className={ui.muted}>{t('eventPage.noProgram')}</p>
          ) : (
            <ol className={styles.program} data-testid="event-program">
              {event.program.map((item, i) => (
                <ProgramLine key={item.id} item={item} number={event.program.slice(0, i + 1).filter((x) => x.kind === 'SONG').length} />
              ))}
            </ol>
          )}
        </section>
      )}

      <EventRoster event={event} />
    </div>
  );
}

function ProgramLine({ item, number }: { item: ProgramItem; number: number }) {
  const { t } = useTranslation();
  const length = item.durationSec !== null ? formatLength(item.durationSec) : '';
  if (item.kind !== 'SONG') {
    return (
      <li className={styles.programOther}>
        <span />
        <span>{item.kind === 'INTERMISSION' ? t('eventPage.intermission') : `${t('eventPage.speech')}: ${item.text ?? ''}`}</span>
        <span>{length}</span>
        <span />
      </li>
    );
  }
  return (
    <li className={styles.programSong}>
      <span className={styles.number}>{number}</span>
      <span>
        {item.songTitle}
        {item.songComposer && <span className={ui.secondary}>{item.songComposer}</span>}
      </span>
      <span>{length}</span>
      <span className={ui.muted}>
        {item.submissionRequested && item.submissionDeadline && t('eventPage.requested', { date: formatDayMonth(item.submissionDeadline, dateLocale()) })}
      </span>
    </li>
  );
}
